import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Circle,
  Clock,
  Calendar,
  User,
  Target,
  TrendingUp,
  FileText,
  Users,
  DollarSign,
  Presentation,
  CheckSquare,
  Square,
  Plus,
  ChevronRight,
  Flag,
  Handshake,
  BarChart3,
  Sparkles,
  Phone,
  Mail,
  MessageSquare,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const API = "/api";

// Default opportunity phases
const DEFAULT_PHASES = [
  { id: "discovery", name: "Discovery", description: "Understand prospect needs and pain points", order: 1 },
  { id: "qualification", name: "Qualification", description: "Confirm budget, authority, need, and timeline", order: 2 },
  { id: "demo", name: "Product Demo", description: "Showcase solution and value proposition", order: 3 },
  { id: "proposal", name: "Proposal", description: "Create and present formal proposal", order: 4 },
  { id: "negotiation", name: "Negotiation", description: "Handle objections and finalize terms", order: 5 },
];

// Default tasks per phase
const DEFAULT_TASKS = {
  discovery: [
    { name: "Schedule discovery call", priority: "high" },
    { name: "Research company background", priority: "medium" },
    { name: "Identify key stakeholders", priority: "high" },
    { name: "Document current pain points", priority: "high" },
    { name: "Understand decision-making process", priority: "medium" },
  ],
  qualification: [
    { name: "Confirm budget range", priority: "high" },
    { name: "Identify decision maker", priority: "high" },
    { name: "Validate timeline urgency", priority: "medium" },
    { name: "Assess competition", priority: "medium" },
    { name: "Score opportunity (BANT)", priority: "high" },
  ],
  demo: [
    { name: "Schedule demo with stakeholders", priority: "high" },
    { name: "Prepare customized demo", priority: "high" },
    { name: "Send pre-demo materials", priority: "medium" },
    { name: "Conduct product demonstration", priority: "high" },
    { name: "Address technical questions", priority: "medium" },
  ],
  proposal: [
    { name: "Create pricing proposal", priority: "high" },
    { name: "Include ROI analysis", priority: "medium" },
    { name: "Get internal approval", priority: "high" },
    { name: "Send proposal to prospect", priority: "high" },
    { name: "Schedule proposal review call", priority: "medium" },
  ],
  negotiation: [
    { name: "Review prospect feedback", priority: "high" },
    { name: "Handle pricing objections", priority: "high" },
    { name: "Negotiate terms", priority: "medium" },
    { name: "Get verbal commitment", priority: "high" },
    { name: "Prepare for contract stage", priority: "medium" },
  ],
};

export default function OpportunityWorkflowPage() {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workflow, setWorkflow] = useState(null);
  const [activePhase, setActivePhase] = useState(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskName, setNewTaskName] = useState("");
  const [newTaskPriority, setNewTaskPriority] = useState("medium");
  
  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchLead();
  }, [leadId, token]);

  const fetchLead = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLead(data);
        
        if (data.opportunity_workflow) {
          setWorkflow(data.opportunity_workflow);
          const firstIncomplete = data.opportunity_workflow.phases?.find(p => p.status !== "completed");
          setActivePhase(firstIncomplete?.id || data.opportunity_workflow.phases?.[0]?.id);
        } else {
          initializeWorkflow(data);
        }
      } else {
        toast.error("Lead not found");
        navigate(-1);
      }
    } catch (error) {
      toast.error("Failed to load lead");
    } finally {
      setLoading(false);
    }
  };

  const initializeWorkflow = (leadData) => {
    const phases = DEFAULT_PHASES.map(phase => ({
      ...phase,
      status: phase.order === 1 ? "in_progress" : "not_started",
      started_at: phase.order === 1 ? new Date().toISOString() : null,
      completed_at: null,
      tasks: (DEFAULT_TASKS[phase.id] || []).map((task, idx) => ({
        id: `task_${phase.id}_${idx}`,
        ...task,
        completed: false,
        completed_at: null,
        completed_by: null,
      }))
    }));

    const wf = {
      started_at: new Date().toISOString(),
      target_close_date: null,
      deal_value: leadData?.budget || null,
      probability: 20,
      phases,
      health: "on_track",
    };
    
    setWorkflow(wf);
    setActivePhase("discovery");
    saveWorkflow(wf);
  };

  const saveWorkflow = async (wfData) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/opportunity-workflow`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ opportunity_workflow: wfData })
      });
      
      if (response.ok) {
        toast.success("Progress saved");
      }
    } catch (error) {
      console.error("Failed to save:", error);
    } finally {
      setSaving(false);
    }
  };

  const toggleTask = async (phaseId, taskId) => {
    const updatedWf = { ...workflow };
    const phase = updatedWf.phases.find(p => p.id === phaseId);
    const task = phase?.tasks.find(t => t.id === taskId);
    
    if (task) {
      task.completed = !task.completed;
      task.completed_at = task.completed ? new Date().toISOString() : null;
      task.completed_by = task.completed ? localStorage.getItem("admin_email") : null;
      
      const allComplete = phase.tasks.every(t => t.completed);
      if (allComplete && phase.status !== "completed") {
        phase.status = "completed";
        phase.completed_at = new Date().toISOString();
        
        // Update probability based on phase completion
        const completedPhases = updatedWf.phases.filter(p => p.status === "completed").length;
        updatedWf.probability = Math.min(20 + (completedPhases * 16), 90);
        
        const nextPhase = updatedWf.phases.find(p => p.order === phase.order + 1);
        if (nextPhase && nextPhase.status === "not_started") {
          nextPhase.status = "in_progress";
          nextPhase.started_at = new Date().toISOString();
          setActivePhase(nextPhase.id);
        }
        
        toast.success(`${phase.name} phase completed!`);
      }
      
      setWorkflow(updatedWf);
      saveWorkflow(updatedWf);
    }
  };

  const updatePhaseStatus = (phaseId, status) => {
    const updatedWf = { ...workflow };
    const phase = updatedWf.phases.find(p => p.id === phaseId);
    
    if (phase) {
      phase.status = status;
      if (status === "in_progress" && !phase.started_at) {
        phase.started_at = new Date().toISOString();
      }
      if (status === "completed") {
        phase.completed_at = new Date().toISOString();
      }
      
      setWorkflow(updatedWf);
      saveWorkflow(updatedWf);
    }
  };

  const addTask = (phaseId) => {
    if (!newTaskName.trim()) return;
    
    const updatedWf = { ...workflow };
    const phase = updatedWf.phases.find(p => p.id === phaseId);
    
    if (phase) {
      phase.tasks.push({
        id: `task_${phaseId}_${Date.now()}`,
        name: newTaskName.trim(),
        priority: newTaskPriority,
        completed: false,
      });
      
      setWorkflow(updatedWf);
      saveWorkflow(updatedWf);
      setNewTaskName("");
      setShowAddTask(false);
      toast.success("Task added");
    }
  };

  const updateHealth = (health) => {
    const updatedWf = { ...workflow, health };
    setWorkflow(updatedWf);
    saveWorkflow(updatedWf);
  };

  const updateProbability = (prob) => {
    const updatedWf = { ...workflow, probability: parseInt(prob) };
    setWorkflow(updatedWf);
    saveWorkflow(updatedWf);
  };

  const updateDealValue = (value) => {
    const updatedWf = { ...workflow, deal_value: value };
    setWorkflow(updatedWf);
    saveWorkflow(updatedWf);
  };

  const getProgressPercentage = () => {
    if (!workflow?.phases) return 0;
    const totalTasks = workflow.phases.reduce((sum, p) => sum + p.tasks.length, 0);
    const completedTasks = workflow.phases.reduce((sum, p) => sum + p.tasks.filter(t => t.completed).length, 0);
    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  const getPhaseProgress = (phase) => {
    const total = phase.tasks.length;
    const completed = phase.tasks.filter(t => t.completed).length;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not set";
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  const currentPhase = workflow?.phases?.find(p => p.id === activePhase);
  const progress = getProgressPercentage();

  return (
    <div className="min-h-screen bg-[#050505] p-6" data-testid="opportunity-workflow-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate(`/admin/leads/${leadId}`)}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to {lead?.first_name}'s Profile
            </button>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Opportunity Workflow</h1>
                <p className="text-zinc-400">{lead?.company_name || lead?.first_name + " " + lead?.last_name}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <select
              value={workflow?.health || "on_track"}
              onChange={(e) => updateHealth(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2 text-white text-sm"
            >
              <option value="on_track">🟢 On Track</option>
              <option value="at_risk">🟡 At Risk</option>
              <option value="blocked">🔴 Blocked</option>
            </select>
            {saving && <Loader2 className="w-4 h-4 animate-spin text-blue-400" />}
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Left Column */}
          <div className="col-span-4 space-y-6">
            {/* Deal Metrics */}
            <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Deal Metrics</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Win Probability</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={workflow?.probability || 20}
                      onChange={(e) => updateProbability(e.target.value)}
                      className="flex-1 accent-blue-500"
                    />
                    <span className="text-xl font-bold text-blue-400 w-14">{workflow?.probability || 20}%</span>
                  </div>
                </div>
                
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Deal Value</label>
                  <input
                    type="text"
                    value={workflow?.deal_value || ""}
                    onChange={(e) => updateDealValue(e.target.value)}
                    placeholder="e.g., $50,000"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Target Close Date</label>
                  <input
                    type="date"
                    value={workflow?.target_close_date || ""}
                    onChange={(e) => {
                      const updatedWf = { ...workflow, target_close_date: e.target.value };
                      setWorkflow(updatedWf);
                      saveWorkflow(updatedWf);
                    }}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Progress */}
            <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Progress</h3>
              
              <div className="relative w-28 h-28 mx-auto mb-4">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="56" cy="56" r="48" fill="none" stroke="#27272a" strokeWidth="10" />
                  <circle
                    cx="56" cy="56" r="48" fill="none"
                    stroke="url(#blueGradient)" strokeWidth="10" strokeLinecap="round"
                    strokeDasharray={`${progress * 3.02} 302`}
                  />
                  <defs>
                    <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#3b82f6" />
                      <stop offset="100%" stopColor="#06b6d4" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">{progress}%</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xl font-bold text-white">{workflow?.phases?.filter(p => p.status === "completed").length || 0}</p>
                  <p className="text-[10px] text-zinc-500">Done</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xl font-bold text-white">{workflow?.phases?.length || 0}</p>
                  <p className="text-[10px] text-zinc-500">Phases</p>
                </div>
              </div>
            </div>

            {/* Phase List */}
            <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Phases</h3>
              
              <div className="space-y-2">
                {workflow?.phases?.map((phase) => (
                  <button
                    key={phase.id}
                    onClick={() => setActivePhase(phase.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                      activePhase === phase.id
                        ? "bg-blue-500/20 border border-blue-500/50"
                        : "bg-zinc-800/50 border border-transparent hover:border-zinc-700"
                    }`}
                  >
                    {phase.status === "completed" ? (
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                    ) : phase.status === "in_progress" ? (
                      <Clock className="w-5 h-5 text-blue-400" />
                    ) : (
                      <Circle className="w-5 h-5 text-zinc-600" />
                    )}
                    <div className="flex-1 text-left">
                      <p className={`text-sm font-medium ${activePhase === phase.id ? "text-blue-300" : "text-white"}`}>
                        {phase.name}
                      </p>
                      <p className="text-xs text-zinc-500">{getPhaseProgress(phase)}%</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${activePhase === phase.id ? "text-blue-400" : "text-zinc-600"}`} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Active Phase */}
          <div className="col-span-8">
            {currentPhase && (
              <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-xl font-bold text-white">{currentPhase.name}</h2>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        currentPhase.status === "completed" ? "bg-green-500/20 text-green-400" :
                        currentPhase.status === "in_progress" ? "bg-blue-500/20 text-blue-400" :
                        "bg-zinc-700/50 text-zinc-400"
                      }`}>
                        {currentPhase.status === "completed" ? "Completed" :
                         currentPhase.status === "in_progress" ? "In Progress" : "Not Started"}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400">{currentPhase.description}</p>
                  </div>
                  
                  {currentPhase.status !== "completed" && (
                    <select
                      value={currentPhase.status}
                      onChange={(e) => updatePhaseStatus(currentPhase.id, e.target.value)}
                      className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      <option value="not_started">Not Started</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Mark Complete</option>
                    </select>
                  )}
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-zinc-400">Phase Progress</span>
                    <span className="text-sm font-medium text-white">{getPhaseProgress(currentPhase)}%</span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300"
                      style={{ width: `${getPhaseProgress(currentPhase)}%` }}
                    />
                  </div>
                </div>

                {/* Tasks */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider">Tasks</h3>
                    <Button
                      onClick={() => setShowAddTask(!showAddTask)}
                      variant="ghost"
                      size="sm"
                      className="text-blue-400 hover:text-blue-300"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Task
                    </Button>
                  </div>

                  {showAddTask && (
                    <div className="flex items-center gap-3 mb-4 p-4 bg-zinc-800/50 rounded-xl">
                      <input
                        type="text"
                        value={newTaskName}
                        onChange={(e) => setNewTaskName(e.target.value)}
                        placeholder="Task name..."
                        className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                      />
                      <select
                        value={newTaskPriority}
                        onChange={(e) => setNewTaskPriority(e.target.value)}
                        className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                      <Button onClick={() => addTask(currentPhase.id)} size="sm" className="bg-blue-500 hover:bg-blue-600">
                        Add
                      </Button>
                    </div>
                  )}

                  <div className="space-y-2">
                    {currentPhase.tasks.map((task) => (
                      <div
                        key={task.id}
                        className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                          task.completed
                            ? "bg-green-500/10 border border-green-500/20"
                            : "bg-zinc-800/50 border border-zinc-700/50 hover:border-zinc-600"
                        }`}
                      >
                        <button onClick={() => toggleTask(currentPhase.id, task.id)} className="flex-shrink-0">
                          {task.completed ? (
                            <CheckSquare className="w-5 h-5 text-green-400" />
                          ) : (
                            <Square className="w-5 h-5 text-zinc-500 hover:text-blue-400" />
                          )}
                        </button>
                        
                        <div className="flex-1">
                          <p className={`text-sm ${task.completed ? "text-zinc-400 line-through" : "text-white"}`}>
                            {task.name}
                          </p>
                          {task.completed && task.completed_by && (
                            <p className="text-xs text-zinc-500 mt-1">
                              Completed by {task.completed_by} on {formatDate(task.completed_at)}
                            </p>
                          )}
                        </div>
                        
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          task.priority === "high" ? "bg-red-500/20 text-red-400" :
                          task.priority === "medium" ? "bg-yellow-500/20 text-yellow-400" :
                          "bg-zinc-700/50 text-zinc-400"
                        }`}>
                          {task.priority}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ZOEY AI Actions - Separate Card */}
            <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800 mt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-gradient-to-r from-yellow-500/20 to-orange-500/20">
                    <Sparkles className="w-4 h-4 text-yellow-400" />
                  </div>
                  <h3 className="font-medium text-white">ZOEY AI Actions</h3>
                </div>
                <span className="text-xs text-zinc-500">Click to open action panel</span>
              </div>
              
              <div className="grid grid-cols-4 gap-3">
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/ai-call`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-emerald-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                    <Phone className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-sm text-zinc-300">AI Call</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/ai-email`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-blue-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-sm text-zinc-300">AI Email</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/ai-text`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-purple-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                    <MessageSquare className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-sm text-zinc-300">AI Text</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/ai-appointment`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-orange-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                    <Calendar className="w-5 h-5 text-orange-400" />
                  </div>
                  <span className="text-sm text-zinc-300">Schedule</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
